import { ArrowUpRight } from "lucide-react";
import { FOCUS_AREAS, PORTFOLIO } from "@/lib/constants";
import { useReveal } from "@/hooks/useReveal";

const PortfolioCard = ({ company, delay = 0 }) => {
  const { ref, shown } = useReveal();
  return (
    <a
      ref={ref}
      href={company.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`portfolio-card-${company.domain}`}
      className={`group border border-border bg-card flex flex-col h-full transition-colors duration-500 hover:border-accent reveal ${
        shown ? "in" : ""
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div
        className={`relative ${company.logoBg} aspect-[4/3] w-full overflow-hidden border-b border-border flex items-center justify-center`}
      >
        <img
          src={company.logo}
          alt={`${company.name} logo`}
          className={`w-full h-full object-cover ${company.objectPosition || "object-center"} transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]`}
          loading="lazy"
        />
      </div>
      <div className="p-8 sm:p-10 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-6">
          <span className="text-[11px] uppercase tracking-[0.3em] text-accent font-semibold">
            {company.category}
          </span>
          <ArrowUpRight
            size={18}
            className="text-primary/40 group-hover:text-accent group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
          />
        </div>
        <h3 className="font-serif text-2xl sm:text-3xl text-primary leading-tight mb-3">
          {company.name}
        </h3>
        <p className="text-xs uppercase tracking-[0.2em] text-primary/55 mb-5">
          {company.domain}
        </p>
        <p className="text-base text-muted-foreground leading-relaxed text-pretty">
          {company.description}
        </p>
        <div className="mt-8 h-px w-12 bg-accent/70 group-hover:w-24 transition-all duration-500" />
      </div>
    </a>
  );
};

export const Portfolio = () => {
  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="py-24 sm:py-32 border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 sm:mb-20">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-10 bg-accent" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-accent font-semibold">
                Select Portfolio
              </span>
            </div>
            <h2
              data-testid="portfolio-headline"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl text-primary leading-[1.05] tracking-tight text-balance"
            >
              Built to own. <br className="hidden sm:block" />
              <em className="italic">Designed to compound.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p
              data-testid="portfolio-intro"
              className="text-lg text-muted-foreground leading-relaxed text-pretty"
            >
              Duneworth builds and owns software platforms across golf,
              marketplaces, data, and adjacent markets. These are the companies
              operating today — selected for long-term ownership, not flipped
              for cycles.
            </p>
          </div>
        </div>

        {/* Focus areas rail (replaces standalone Platforms section) */}
        <div
          data-testid="focus-areas-rail"
          className="mb-16 sm:mb-20 border-t border-b border-border grid grid-cols-2 lg:grid-cols-4"
        >
          {FOCUS_AREAS.map((area, i) => {
            const mobileNeedsLeft = i % 2 === 1;
            const mobileNeedsTop = i >= 2;
            const lgNeedsLeft = i !== 0;
            const cls = [
              "px-6 py-6 sm:py-8 flex items-center gap-4",
              mobileNeedsLeft ? "border-l border-border" : "",
              mobileNeedsTop ? "border-t border-border lg:border-t-0" : "",
              lgNeedsLeft ? "lg:border-l lg:border-border" : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <div key={area.label} data-testid={`focus-area-${i + 1}`} className={cls}>
                <span className="text-[11px] uppercase tracking-[0.3em] text-accent font-semibold shrink-0">
                  0{i + 1}
                </span>
                <span className="text-sm sm:text-base font-serif text-primary leading-snug">
                  {area.label}
                </span>
              </div>
            );
          })}
        </div>

        <div className="grid sm:grid-cols-2 gap-px bg-border">
          {PORTFOLIO.map((company, i) => (
            <PortfolioCard key={company.domain} company={company} delay={i * 100} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
